// === Admin: Pre Check ===
// Full CRUD page — list, add, edit, soft-delete.
// Modelled on the Daily Check List page (pages/daily-check-list.tsx).

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PreCheckRow {
  readonly id: number;
  readonly description: string;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly created_by: string | null;
  readonly updated_by: string | null;
}

interface FormState {
  description: string;
}

const DEFAULT_FORM: FormState = { description: '' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium' }).format(new Date(iso));
}

function fmtAuDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).format(d);
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function PreCheckPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  // ---- List state ----
  const [rows, setRows] = useState<readonly PreCheckRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Add / Edit dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [auditInfo, setAuditInfo] = useState<{
    created_at: string | null; updated_at: string | null;
    created_by: string | null; updated_by: string | null;
  } | null>(null);

  // ---- Delete dialog ----
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/pre-check`);
      url.searchParams.set('page', String(page + 1));
      url.searchParams.set('limit', String(rowsPerPage));
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);

      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: PreCheckRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load pre check list');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // ---------------------------------------------------------------------------
  // Open Add / Edit
  // ---------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setFormError(null);
    setAuditInfo(null);
    setDialogOpen(true);
  };

  const openEdit = async (id: number) => {
    if (!token) return;
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/pre-check/${id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: PreCheckRow };
      setForm({ description: json.data.description });
      setAuditInfo({
        created_at: json.data.created_at ?? null,
        updated_at: json.data.updated_at ?? null,
        created_by: json.data.created_by ?? null,
        updated_by: json.data.updated_by ?? null,
      });
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const trimmed = form.description.trim();
    if (!trimmed) { setFormError('Description is required.'); return; }
    if (trimmed.length > 500) { setFormError('Description must be 500 characters or fewer.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      const url = editingId
        ? `${getApiUrl()}/api/v1/admin/pre-check/${editingId}`
        : `${getApiUrl()}/api/v1/admin/pre-check`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ description: trimmed }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      showSnack(editingId ? 'Pre check item updated.' : 'Pre check item added.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: number, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/pre-check/${deleteId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      showSnack('Pre check item deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Pre Check</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage the pre check list items
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Item
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search descriptions…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>
                Clear
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell>Description</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 140 }}>Created</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 140 }}>Updated</AppTableHeadCell>
                  <AppTableHeadCell align="right" sx={{ width: 96 }}>Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <AppTableRow key={row.id}>
                      <TableCell sx={{ fontWeight: 500, whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.description}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {fmtDate(row.created_at)}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {fmtDate(row.updated_at)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => void openEdit(row.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => confirmDelete(row.id, row.description)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </AppTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}
            >
              {loading ? 'Loading…' : total === 0 ? 'No records found'
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    opacity: 0.85,
                    whiteSpace: 'nowrap',
                  })}
                >
                  Show
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    opacity: 0.85,
                    whiteSpace: 'nowrap',
                  })}
                >
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Pre Check Item' : 'Add Pre Check Item'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Description"
            size="small"
            fullWidth
            required
            placeholder="e.g. Check tyre pressure on all vehicles"
            inputProps={{ maxLength: 500 }}
            multiline
            minRows={2}
            value={form.description}
            onChange={(e) => setForm({ description: e.target.value })}
            autoFocus
          />

          {/* Audit Trail */}
          {editingId && auditInfo && (
            <Box
              mt={1}
              p={2}
              sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                Audit Trail
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(auditInfo.created_at)}{auditInfo.created_by ? <> &mdash; <strong>{auditInfo.created_by}</strong></> : ''}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(auditInfo.updated_at)}{auditInfo.updated_by ? <> &mdash; <strong>{auditInfo.updated_by}</strong></> : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : undefined}
          >
            {editingId ? 'Save Changes' : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Pre Check Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteLabel}</strong>? This cannot be undone and will remove the
            item from the pre check list.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
